import { Link } from "react-router-dom";
import { MessageCircle, Mail, MapPin, Anchor } from "lucide-react";
import Logo from "@/components/Logo";

const WA_LINK = "https://wa.me/905316224378";

const COPY = {
  ru: {
    tagline: "Премиум цветочное оформление яхт в Бодруме и на Миконосе: предложения, дни рождения, годовщины и чартеры.",
    h_cities: "Направления",
    h_services: "Услуги",
    h_company: "Компания",
    h_contacts: "Контакты",
    cities: [
      { name: "Бодрум", slug: "bodrum" },
      { name: "Миконос", slug: "mykonos" },
    ],
    services: [
      { name: "Оформление яхт", href: (l) => `/yachts${l !== "ru" ? `?lang=${l}` : ""}` },
      { name: "Свадебная флористика", href: (l) => `/weddings${l !== "ru" ? `?lang=${l}` : ""}` },
      { name: "Магазин букетов", href: (l) => `/${l !== "ru" ? `?lang=${l}` : ""}` },
    ],
    company: [
      { name: "Блог", href: (l) => `/blog${l !== "ru" ? `?lang=${l}` : ""}` },
      { name: "Политика конфиденциальности", href: (l) => `/legal/privacy${l !== "ru" ? `?lang=${l}` : ""}` },
      { name: "Условия использования", href: (l) => `/legal/terms${l !== "ru" ? `?lang=${l}` : ""}` },
    ],
    rights: "Все права защищены.",
    slogan: "ЦВЕТЫ, КОТОРЫЕ СОЗДАЮТ МОМЕНТЫ",
    location: "Бодрум · Миконос · Средиземноморье",
  },
  en: {
    tagline: "Premium floral decoration for yachts in Bodrum and Mykonos: proposals, birthdays, anniversaries and charters.",
    h_cities: "Destinations",
    h_services: "Services",
    h_company: "Company",
    h_contacts: "Contacts",
    cities: [
      { name: "Bodrum", slug: "bodrum" },
      { name: "Mykonos", slug: "mykonos" },
    ],
    services: [
      { name: "Yacht flowers", href: (l) => `/yachts${l !== "ru" ? `?lang=${l}` : ""}` },
      { name: "Wedding floristry", href: (l) => `/weddings${l !== "ru" ? `?lang=${l}` : ""}` },
      { name: "Bouquet shop", href: (l) => `/${l !== "ru" ? `?lang=${l}` : ""}` },
    ],
    company: [
      { name: "Blog", href: (l) => `/blog${l !== "ru" ? `?lang=${l}` : ""}` },
      { name: "Privacy Policy", href: (l) => `/legal/privacy${l !== "ru" ? `?lang=${l}` : ""}` },
      { name: "Terms of Service", href: (l) => `/legal/terms${l !== "ru" ? `?lang=${l}` : ""}` },
    ],
    rights: "All rights reserved.",
    slogan: "FLOWERS THAT CREATE MOMENTS",
    location: "Bodrum · Mykonos · Mediterranean",
  },
};

export default function YachtsFooter({ lang = "ru" }) {
  const t = COPY[lang] || COPY.ru;

  return (
    <footer className="relative overflow-hidden bg-[#FFF6F8] text-[#1F1815] border-t border-[#F3DDE1]" data-testid="yachts-footer">
      <div className="absolute -top-24 right-[-6%] w-[420px] h-[420px] rounded-full bg-[#F6C9CF]/30 blur-3xl pointer-events-none" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10 py-14 sm:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-10 sm:gap-12">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-2">
            <Logo />
            <p className="mt-5 sm:mt-6 text-sm text-[#6B5D4F] font-light leading-relaxed max-w-sm">{t.tagline}</p>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
               className="mt-7 inline-flex items-center gap-2 sm:gap-3 px-6 py-3 bg-gradient-to-r from-[#F2B8C4] via-[#E39FAC] to-[#C25E72] hover:from-[#F7C6CF] hover:to-[#D98096] text-[#1F1815] text-[11px] tracking-[0.22em] font-semibold transition-all rounded-xl shadow-[0_10px_30px_-12px_rgba(194,94,114,0.6)]">
              <MessageCircle className="w-4 h-4" /> WHATSAPP
            </a>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="text-[10px] tracking-[0.3em] text-[#C25E72] mb-4 sm:mb-5 font-medium">{t.h_cities.toUpperCase()}</h4>
            <ul className="space-y-2.5 text-sm font-light">
              {t.cities.map((c) => (
                <li key={c.slug}>
                  <Link to={`/${c.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`} className="text-[#6B5D4F] hover:text-[#C25E72] transition inline-flex items-center gap-1.5">
                    <Anchor className="w-3 h-3 text-[#E39FAC]" /> {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-[10px] tracking-[0.3em] text-[#C25E72] mb-4 sm:mb-5 font-medium">{t.h_services.toUpperCase()}</h4>
            <ul className="space-y-2.5 text-sm font-light">
              {t.services.map((s, i) => (
                <li key={i}>
                  <Link to={s.href(lang)} className="text-[#6B5D4F] hover:text-[#C25E72] transition">{s.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company + Contacts */}
          <div>
            <h4 className="text-[10px] tracking-[0.3em] text-[#C25E72] mb-4 sm:mb-5 font-medium">{t.h_company.toUpperCase()}</h4>
            <ul className="space-y-2.5 text-sm font-light">
              {t.company.map((c, i) => (
                <li key={i}>
                  <Link to={c.href(lang)} className="text-[#6B5D4F] hover:text-[#C25E72] transition">{c.name}</Link>
                </li>
              ))}
            </ul>
            <h4 className="text-[10px] tracking-[0.3em] text-[#C25E72] mt-7 mb-4 font-medium">{t.h_contacts.toUpperCase()}</h4>
            <ul className="space-y-2 text-sm font-light">
              <li className="flex items-center gap-2 text-[#6B5D4F]">
                <MessageCircle className="w-3.5 h-3.5 text-[#E39FAC]" />
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-[#C25E72] transition">+90 531 622 43 78</a>
              </li>
              <li className="flex items-center gap-2 text-[#6B5D4F]">
                <Mail className="w-3.5 h-3.5 text-[#E39FAC]" />
                <a href="mailto:info@yuccaflower.com" className="hover:text-[#C25E72] transition">info@yuccaflower.com</a>
              </li>
              <li className="flex items-center gap-2 text-[#8A7C72] text-xs">
                <MapPin className="w-3.5 h-3.5 text-[#E39FAC]" />
                {t.location}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-[#F3DDE1] flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#8A7C72] font-light">
          <span>&copy; 2026 YUCCA Flowers. {t.rights}</span>
          <span className="text-[10px] tracking-[0.25em] text-[#C25E72]">{t.slogan}</span>
        </div>
      </div>
    </footer>
  );
}
